import { API_BASE_URL } from '../config/api';

const supportedImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
const supportedImageExtensions = /\.(jpe?g|png|webp)$/i;

export const extractReceipt = async (file, sessionId) => {
  const formData = new FormData();
  formData.append('receiptImage', file);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 50000);
  try {
    const response = await fetch(`${API_BASE_URL}/expense/receipt/extract`, {
      method: 'POST',
      headers: { 'session-id': sessionId },
      body: formData,
      signal: controller.signal
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.error || `Request failed (${response.status})`);
    }
    return result;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Receipt scanning timed out; try again');
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const matchAircraftId = (identifier, aircraft) => {
  if (!identifier || !Array.isArray(aircraft)) return undefined;
  const normalize = value => String(value || '').trim().toUpperCase();
  const target = normalize(identifier);
  const exactMatch = aircraft.find(plane => [plane.id, plane.tailNumber, plane.registration].some(value => normalize(value) === target));
  if (exactMatch) return exactMatch.id;

  const partialMatches = aircraft.filter(plane => [plane.tailNumber, plane.registration]
    .some(value => {
      const normalizedValue = normalize(value);
      return normalizedValue && (normalizedValue.includes(target) || target.includes(normalizedValue));
    }));
  return partialMatches.length === 1 ? partialMatches[0].id : undefined;
};

export const mapReceiptToExpense = (result, aircraft) => {
  const updates = {};
  if (result.date) updates.date = result.date;
  if (result.cost != null) updates.total = result.cost;
  if (result.merchant) updates.vendor = result.merchant;
  if (result.expenseType) {
    updates.expense = result.expenseType === 'fuel'
      ? 'Aviation fuel'
      : result.expenseType.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
  }
  const aircraftId = matchAircraftId(result.aircraftId, aircraft);
  if (aircraftId != null) updates.aircraftId = aircraftId;
  return updates;
};

export const extractExpenseReceipt = async (file, sessionId, aircraft = [], airports = '') => {
  const isSupportedImage = supportedImageTypes.includes(file?.type?.toLowerCase())
    || supportedImageExtensions.test(file?.name || '');
  if (!isSupportedImage) {
    return null;
  }

  const details = await extractReceipt(file, sessionId);
  if (!details) {
    return null;
  }

  const updates = mapReceiptToExpense(details, aircraft);
  if (details.expenseType === 'fuel') {
    const merchant = details.merchant == null ? '' : String(details.merchant);
    const airportCodes = Array.isArray(airports) ? airports : String(airports).split(',');
    const hasAirportEvidence = [details.airport, details.airportCode, details.location]
      .some((value) => value != null && String(value).trim() !== '')
      || airportCodes.some((code) => {
        const normalizedCode = String(code).trim();
        return normalizedCode && merchant.toUpperCase().includes(normalizedCode.toUpperCase());
      });
    const hasAircraftId = details.aircraftId != null && String(details.aircraftId).trim() !== '';
    if (!(hasAircraftId || hasAirportEvidence)) {
      updates.expense = details.expenseType;
    }
  }

  return updates;
};